import { createRouter, createWebHistory } from "vue-router";

import HeaderWrapper from "./components/web/HeaderWrapper.vue";
import HeaderMenuWrapper from "./components/web/HeaderMenuWrapper.vue";
import FooterWrapper from "./components/web/FooterWrapper.vue";
import HomeWrapper from "./components/web/HomeWrapper.vue";
import JobApplicationWrapper from "./components/web/JobApplicationWrapper.vue";
import ContactUsWrapper from "./components/web/ContactUsWrapper.vue";
import PartnershipProgrammerWrapper from "./components/web/PartnershipProgrammerWrapper.vue";
import PartnershipEntrepreneurWrapper from "./components/web/PartnershipEntrepreneurWrapper.vue";
import ServicesWrapper from "./components/web/ServicesWrapper.vue";
import ClientsWrapper from "./components/web/ClientsWrapper.vue";
import ProjectsWrapper from "./components/web/ProjectsWrapper.vue";
import TrainingAndMentoringProgramWrapper from "./components/web/TrainingAndMentoringProgramWrapper.vue";
import SelfLearningResourcesWrapper from "./components/web/SelfLearningResourcesWrapper.vue";
import DeliveryPolicyWrapper from "./components/web/DeliveryPolicyWrapper.vue";
import RefundsPolicyWrapper from "./components/web/RefundsPolicyWrapper.vue";
import Error404Wrapper from "./components/errors/Error404Wrapper.vue";
import Error500Wrapper from "./components/errors/Error500Wrapper.vue";

// Auto-detect base path from script location or use configured value
function detectBasePath() {
  // First try VITE_PUBLIC_PATH from environment
  // Always use it if defined, even if it's "/" to avoid auto-detection issues in dev mode
  if (import.meta.env.VITE_PUBLIC_PATH !== undefined) {
    return import.meta.env.VITE_PUBLIC_PATH;
  }

  // Try to detect from current script location
  const scripts = document.getElementsByTagName("script");
  for (let i = 0; i < scripts.length; i++) {
    const src = scripts[i].src;
    if (src && (src.includes("web.js") || src.includes("web.min.js"))) {
      // Extract path before /js/web.js or /js/web.min.js
      const match = src.match(/^https?:\/\/[^/]+(\/.*?)\/js\/web/);
      if (match && match[1] && match[1] !== "") {
        return match[1] + "/";
      }
    }
  }

  // Fallback to root
  return "/";
}

const BASE_PATH = detectBasePath();

// Locale validation guard
function validateLocale(to) {
  const locale = to.params.locale;

  // Validate locale parameter
  if (locale && (locale === "bg" || locale === "en")) {
    // Valid locale, update localStorage
    localStorage.setItem("locale", locale);
    return true;
  } else if (locale) {
    // Invalid locale, redirect to Bulgarian version
    return { name: to.name, params: { ...to.params, locale: "bg" } };
  } else {
    // No locale specified, shouldn't happen with our routes
    return { name: "home", params: { locale: "bg" } };
  }
}

const AppRoutes = [
  // Default redirect: / → /bg/home (or saved locale)
  {
    path: "/",
    redirect: () => {
      // Check localStorage or default to Bulgarian
      const savedLocale = localStorage.getItem("locale");
      const locale = savedLocale === "bg" || savedLocale === "en" ? savedLocale : "bg";
      return `/${locale}/home`;
    },
  },

  // Bare locale redirects: /bg → /bg/home, /en → /en/home
  {
    path: "/bg",
    redirect: "/bg/home",
  },
  {
    path: "/en",
    redirect: "/en/home",
  },

  // Locale-prefixed routes
  {
    path: "/:locale/error-404",
    name: "error404",
    meta: { requiresAuth: false },
    beforeEnter: validateLocale,
    components: {
      default: Error404Wrapper,
      headerView: HeaderWrapper,
      headerMenuView: HeaderMenuWrapper,
      footerView: FooterWrapper,
    },
  },
  {
    path: "/:locale/error-500",
    name: "error500",
    meta: { requiresAuth: false },
    beforeEnter: validateLocale,
    components: {
      default: Error500Wrapper,
      headerView: HeaderWrapper,
      headerMenuView: HeaderMenuWrapper,
      footerView: FooterWrapper,
    },
  },
  {
    path: "/:locale/refunds-policy",
    name: "refundsPolicy",
    meta: { requiresAuth: false },
    beforeEnter: validateLocale,
    components: {
      default: RefundsPolicyWrapper,
      headerView: HeaderWrapper,
      headerMenuView: HeaderMenuWrapper,
      footerView: FooterWrapper,
    },
  },
  {
    path: "/:locale/delivery-policy",
    name: "deliveryPolicy",
    meta: { requiresAuth: false },
    beforeEnter: validateLocale,
    components: {
      default: DeliveryPolicyWrapper,
      headerView: HeaderWrapper,
      headerMenuView: HeaderMenuWrapper,
      footerView: FooterWrapper,
    },
  },
  {
    path: "/:locale/home",
    name: "home",
    meta: { requiresAuth: false },
    beforeEnter: validateLocale,
    components: {
      default: HomeWrapper,
      headerView: HeaderWrapper,
      headerMenuView: HeaderMenuWrapper,
      footerView: FooterWrapper,
    },
  },
  {
    path: "/:locale/job-application",
    name: "jobApplication",
    meta: { requiresAuth: false },
    beforeEnter: validateLocale,
    components: {
      default: JobApplicationWrapper,
      headerView: HeaderWrapper,
      headerMenuView: HeaderMenuWrapper,
      footerView: FooterWrapper,
    },
  },
  {
    path: "/:locale/contact-us",
    name: "contactUs",
    meta: { requiresAuth: false },
    beforeEnter: validateLocale,
    components: {
      default: ContactUsWrapper,
      headerView: HeaderWrapper,
      headerMenuView: HeaderMenuWrapper,
      footerView: FooterWrapper,
    },
  },
  {
    path: "/:locale/partnership-programmer",
    name: "partnershipProgrammer",
    meta: { requiresAuth: false },
    beforeEnter: validateLocale,
    components: {
      default: PartnershipProgrammerWrapper,
      headerView: HeaderWrapper,
      headerMenuView: HeaderMenuWrapper,
      footerView: FooterWrapper,
    },
  },
  {
    path: "/:locale/partnership-entrepreneur",
    name: "partnershipEntrepreneur",
    meta: { requiresAuth: false },
    beforeEnter: validateLocale,
    components: {
      default: PartnershipEntrepreneurWrapper,
      headerView: HeaderWrapper,
      headerMenuView: HeaderMenuWrapper,
      footerView: FooterWrapper,
    },
  },
  {
    path: "/:locale/services",
    name: "services",
    meta: { requiresAuth: false },
    beforeEnter: validateLocale,
    components: {
      default: ServicesWrapper,
      headerView: HeaderWrapper,
      headerMenuView: HeaderMenuWrapper,
      footerView: FooterWrapper,
    },
  },
  {
    path: "/:locale/clients",
    name: "clients",
    meta: { requiresAuth: false },
    beforeEnter: validateLocale,
    components: {
      default: ClientsWrapper,
      headerView: HeaderWrapper,
      headerMenuView: HeaderMenuWrapper,
      footerView: FooterWrapper,
    },
  },
  {
    path: "/:locale/projects",
    name: "projects",
    meta: { requiresAuth: false },
    beforeEnter: validateLocale,
    components: {
      default: ProjectsWrapper,
      headerView: HeaderWrapper,
      headerMenuView: HeaderMenuWrapper,
      footerView: FooterWrapper,
    },
  },
  {
    path: "/:locale/training-and-mentoring-program",
    name: "trainingAndMentoringProgram",
    meta: { requiresAuth: false },
    beforeEnter: validateLocale,
    components: {
      default: TrainingAndMentoringProgramWrapper,
      headerView: HeaderWrapper,
      headerMenuView: HeaderMenuWrapper,
      footerView: FooterWrapper,
    },
  },
  {
    path: "/:locale/self-learning-resources",
    name: "selfLearningResources",
    meta: { requiresAuth: false },
    beforeEnter: validateLocale,
    components: {
      default: SelfLearningResourcesWrapper,
      headerView: HeaderWrapper,
      headerMenuView: HeaderMenuWrapper,
      footerView: FooterWrapper,
    },
    children: [
      {
        path: ":ids",
        components: {
          default: SelfLearningResourcesWrapper,
          headerView: HeaderWrapper,
          headerMenuView: HeaderMenuWrapper,
          footerView: FooterWrapper,
        },
      },
    ],
  },

  // Catch-all: redirect to 404 with default locale
  {
    path: "/:pathMatch(.*)*",
    redirect: () => {
      const savedLocale = localStorage.getItem("locale");
      const locale = savedLocale === "bg" || savedLocale === "en" ? savedLocale : "bg";
      return `/${locale}/error-404`;
    },
  },
];

export default createRouter({
  routes: AppRoutes,
  history: createWebHistory(BASE_PATH),
});
